import { useState } from 'react';
import { CONTACT_INFO, SITE_NAME } from '@/utils/site';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/d9v9sk03256a3o8koh10';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honey = ((formData.get('website_alt') as string) ?? '').trim();

    setStatus('submitting');
    setFormError('');

    if (honey) {
      setStatus('success');
      form.reset();
      window.setTimeout(() => setStatus('idle'), 4000);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim()) {
        payload.append(key, value.trim());
      }
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText;
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(String(serverMsg || '提交失败，请稍后再试'));
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试');
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20" aria-label="联系我们">
      <div className="text-center">
        <span className="text-xs font-medium uppercase tracking-widest text-primary-600">Contact</span>
        <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">联系我们</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-600">
          无论是作品反馈、版权合作还是商务洽谈，欢迎通过以下方式联系073漫画网团队，我们会在1-3个工作日内回复。
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
        <div className="space-y-3">
          {[
            {
              icon: 'ri-mail-line',
              label: '电子邮箱',
              value: CONTACT_INFO.email,
              hint: '适用于版权合作、内容反馈',
            },
            {
              icon: 'ri-phone-line',
              label: '联系电话',
              value: CONTACT_INFO.phone,
              hint: '工作日 09:00 - 18:00',
            },
            {
              icon: 'ri-wechat-line',
              label: '微信公众号',
              value: CONTACT_INFO.wechat,
              hint: '扫码关注，获取每日更新提醒',
            },
            {
              icon: 'ri-map-pin-2-line',
              label: '公司地址',
              value: CONTACT_INFO.address,
              hint: CONTACT_INFO.hours,
            },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-4 rounded-lg bg-background-100 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                <i className={item.icon} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 className="text-xs font-medium uppercase tracking-wide text-foreground-600">
                  {item.label}
                </h3>
                <p className="mt-1 break-all text-sm font-semibold text-foreground-950">{item.value}</p>
                <p className="mt-0.5 text-xs text-foreground-600">{item.hint}</p>
              </div>
            </div>
          ))}

          <div className="rounded-lg bg-secondary-100 p-4">
            <p className="text-xs leading-relaxed text-secondary-900">
              <i className="ri-information-line mr-1" aria-hidden="true" />
              提示：提交留言前请确认联系方式填写无误，以便我们及时与您取得联系。您的信息仅用于沟通回复，不会泄露给任何第三方。
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form={SUBMIT_ADDR}
          className="rounded-lg bg-background-50 p-5 shadow-soft md:p-6"
        >
          <h3 className="font-heading text-lg text-foreground-950 md:text-xl">在线留言</h3>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-800">
                您的称呼
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                maxLength={30}
                placeholder="请输入您的称呼"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-800">
                邮箱地址
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                maxLength={60}
                placeholder="用于接收回复"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="contact-phone" className="mb-1.5 block text-sm text-foreground-800">
              联系电话
            </label>
            <input
              id="contact-phone"
              name="phone"
              type="tel"
              maxLength={20}
              placeholder="选填，便于电话沟通"
              className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>

          <div className="mt-4">
            <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-800">
              留言内容
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={5}
              maxLength={500}
              placeholder="请描述您的需求或反馈（500字以内）"
              className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
            <p className="mt-1 text-right text-xs text-foreground-500">最多500字</p>
          </div>

          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field"
          />

          {status === 'success' && (
            <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
              <i className="ri-checkbox-circle-fill" aria-hidden="true" />
              提交成功！感谢您的留言，我们会尽快与您联系。
            </p>
          )}
          {status === 'error' && (
            <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
              <i className="ri-error-warning-fill" aria-hidden="true" />
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'submitting' ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" aria-hidden="true" />
                提交留言
              </>
            )}
          </button>

          <p className="mt-3 text-center text-xs text-foreground-600">
            也可直接发送邮件至 {CONTACT_INFO.email}，主题注明“{SITE_NAME}联系”。
          </p>
        </form>
      </div>
    </section>
  );
}